import { useEffect, useState } from "react";
import { skipReasonLabel } from "../lib/constants";
import { GOLD, NAVY, WHITE } from "../styles/palette";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { fetchClientVisits, summarizeClientVisits } from "../lib/visitTimes";
import { DEO_FREQ_OPTIONS, DEO_PER_VISIT, getDeoMonthly, getPerVisit, getPrice } from "../lib/pricing";
import { DAYS, dayTitle, getDayForZip, getServiceDayForClient, hasDayOverride } from "../lib/serviceArea";
import { parityFromDate } from "../lib/date";
import { JobPhoto } from "./JobPhoto";
import { PaymentLinkModal } from "./PaymentLinkModal";
import { ClientBillingTab } from "./ClientBillingTab";
import { SkipVisitModal } from "./SkipVisitModal";
import { ChargeClientModal } from "./ChargeClientModal";
import { JobDetailModal } from "./JobDetailModal";
import { ACTIVE_THEME } from "../themes";
import { useBackClose } from "../lib/navStack";
import { useIsMobile } from "../lib/useIsMobile";

export function ClientProfile({ pricing, client, invoices, onBack, onStatusChange, onScheduleChange, onContactUpdate, onClientRefreshed, onDeoAction, zipSecondDay = {}, showToast, onRefreshInvoices, onAddManualJob, technicians, onAssignTech, onApprove, onRetryCharge, onIssueRefund, onVoidInvoice, onEditInvoice, orgName, credits = [], onRefreshCredits }) {
  const isMobile = useIsMobile();
  const [tab,         setTab]         = useState("overview");
  const [history,     setHistory]     = useState([]);
  const [loadingHist, setLoadingHist] = useState(true);
  const [editContact,  setEditContact]  = useState(false);
  const [smsConsent,   setSmsConsent]   = useState(!!client.sms_consent);
  const [reversingId,  setReversingId]  = useState(null);
  // This client's active (positive, non-reversed) credits, newest first.
  const clientCredits = (credits||[]).filter(c => c.client_id===client.id && Number(c.amount)>0 && !c.reversed_at);
  const [editPlan,     setEditPlan]     = useState(false);
  const [showCharge,   setShowCharge]   = useState(false);
  const [subscription, setSubscription] = useState(null); // from subscriptions table
  const [loadingSub,   setLoadingSub]   = useState(true);
  const [selectedTech,    setSelectedTech]    = useState(client.technician_id||"");
  const [clientNotes,     setClientNotes]     = useState(client.notes||"");
  const [visitNotes,      setVisitNotes]      = useState(client.visit_notes||"");
  const [savingNotes,     setSavingNotes]     = useState(false);
  const [generatingCardLink, setGeneratingCardLink] = useState(false);

  // Load subscription — sync from Stripe first, then read from DB
  useEffect(()=>{
    if (!client.stripe_subscription_id) { setLoadingSub(false); return; }
    setLoadingSub(true);
    // 1. Trigger sync from Stripe (waits for completion)
    fetch(`${SUPABASE_URL}/functions/v1/sync-subscriptions`,{
      method:"POST",
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
      body:JSON.stringify({ client_id: client.id }),
    }).catch(()=>{}).finally(()=>{
      // 2. Read fresh data from DB after sync
      fetch(`${SUPABASE_URL}/rest/v1/subscriptions?client_id=eq.${client.id}&order=updated_at.desc`,{
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
      }).then(r=>r.json()).then(rows=>{
        if (Array.isArray(rows)) {
          // Plan view shows the CLEANUP sub specifically — not whichever row synced
          // last (that grabbed the deo sub and showed its $ as the cleanup price).
          const cleanup = rows.find(s=>s.service_type==="cleanup") || rows.find(s=>s.service_type!=="deo") || rows[0] || null;
          setSubscription(cleanup);
        }
        setLoadingSub(false);
      }).catch(()=>setLoadingSub(false));
    });
  },[client.id,client.stripe_subscription_id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveNotes() {
    setSavingNotes(true);
    await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${client.id}`,{
      method:"PATCH",
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
      body:JSON.stringify({notes:clientNotes,visit_notes:visitNotes}),
    });
    showToast("Notes saved");
    setSavingNotes(false);
  }

  // "Copy card link": mints a short https://iscoopcrm.com/c/<code> link (create-card-link, sql/090) that lets
  // the customer add/replace a card for 24 h — no login, no verification code — and copies it so the owner can
  // paste it into a GHL text. NOTHING is sent to the customer from here. A new link replaces the previous one.
  async function generateCardUpdateLink() {
    if (generatingCardLink) return;
    setGeneratingCardLink(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-card-link`, {
        method: "POST",
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ client_id: client.id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.url) throw new Error(body.error || "Could not create the card link");

      try {
        await navigator.clipboard.writeText(body.url);
        showToast("Card link copied — paste it into a text. Works for 24 hours.");
      } catch {
        window.prompt("Copy this card link (works for 24 hours):", body.url);
      }
    } catch (e) {
      showToast(e.message || "Could not create the card link", "error");
    } finally {
      setGeneratingCardLink(false);
    }
  }
  const [assigningTech,   setAssigningTech]   = useState(false);
  const [sendPaymentLink,  setSendPaymentLink]  = useState(false);
  const [showPauseReason,  setShowPauseReason]  = useState(false);
  const [pauseReason,      setPauseReason]      = useState("");
  const [clientType,    setClientType]    = useState(client.client_type||"residential");
  const [savingType,    setSavingType]    = useState(false);
  const [visits,        setVisits]        = useState([]);
  const [visitRecords,  setVisitRecords]  = useState({}); // eslint-disable-line no-unused-vars
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [headerPhotos,  setHeaderPhotos]  = useState({}); // scheduled_date -> job_headers.photo_url (the canonical one-per-job proof photo)
  const [headerDur,     setHeaderDur]     = useState({}); // scheduled_date -> job_headers.duration_mins (job duration: first start -> last finish)
  const [skipModal,     setSkipModal]     = useState(null);
  const [visitReassign, setVisitReassign] = useState(null); // "date+serviceType" key
  const [jobDetailId,   setJobDetailId]   = useState(null); // open JobDetailModal for this job.id
  // Browser Back closes an open profile modal before it leaves the profile (see navStack).
  useBackClose(showCharge, () => setShowCharge(false));
  useBackClose(sendPaymentLink, () => setSendPaymentLink(false));
  useBackClose(showPauseReason, () => setShowPauseReason(false));
  useBackClose(!!skipModal, () => setSkipModal(null));
  useBackClose(!!jobDetailId, () => setJobDetailId(null));
  const [contactForm, setContactForm] = useState({ name:client.name||"", email:client.email||"", phone:client.phone||"", phone_secondary:client.phone_secondary||"", address:client.address||"", zip:client.zip||"", referred_by:client.referred_by||"" });
  const [planForm,    setPlanForm]    = useState({ frequency:client.frequency||"weekly", dogs:client.dogs||1, deo_enabled:client.deo_enabled||false, deo_frequency:client.deo_frequency||"biweekly" });
  const [saving,      setSaving]      = useState(false);

  // Effective day = the per-client override if set, else the zip's day. `zipDay` is kept
  // separately so the override editor can show what they'd fall back to if cleared.
  const zipDay       = getDayForZip(client.zip);
  // The zip's SECOND day (twice-weekly only). serviceArea is keyed by day so it loses this,
  // hence App loads a separate {zip: second_day} map.
  const zipSecond    = zipSecondDay?.[client.zip] || null;
  const serviceDay   = getServiceDayForClient(client);
  const overridden   = hasDayOverride(client);
  const [dayBusy,    setDayBusy]    = useState(false);
  const [editingDay, setEditingDay] = useState(false);

  // Changing the day changes which route this client lands on, so the existing future
  // AUTO visits are wrong the moment it's saved. Mirror the zip-change path: drop the
  // future scheduled auto visits, then rebuild this month + next from today. Manual,
  // completed and past visits are never touched.
  // EVERY schedule-shaping field goes through here: PATCH the client, drop their FUTURE
  // scheduled AUTO visits, then rebuild this month + next from today. Manual, completed
  // and past visits are never touched.
  //
  // Kept in one place deliberately — a control that saves WITHOUT rescheduling leaves the
  // profile claiming one thing while the route still does the old thing, which is exactly
  // how a client ends up a week out of phase and nobody notices for a month.
  async function saveAndReschedule(patch, successMsg) {
    const H = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` };
    const today = new Date().toISOString().slice(0, 10);
    const r = await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${client.id}`, {
      method: "PATCH",
      headers: { ...H, "Content-Type": "application/json", Prefer: "return=minimal" },
      body: JSON.stringify(patch),
    });
    if (!r.ok) throw new Error(await r.text());
    await fetch(`${SUPABASE_URL}/rest/v1/jobs?client_id=eq.${client.id}&scheduled_date=gte.${today}&status=eq.scheduled&source=eq.auto`,
      { method: "DELETE", headers: H }).catch(() => {});
    const now = new Date();
    for (const m of [0, 1]) {
      const d = new Date(now.getFullYear(), now.getMonth() + m, 1);
      await fetch(`${SUPABASE_URL}/functions/v1/generate-monthly-jobs`, {
        method: "POST", headers: { ...H, "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: client.id, year: d.getFullYear(), month: d.getMonth(), from_date: today }),
      }).catch(() => {});
    }
    showToast?.(successMsg);
    await onClientRefreshed?.(client.id);
  }

  async function saveDayOverride(day) {
    setDayBusy(true);
    try {
      // Clearing the primary day also clears the second — a second day with no first
      // is meaningless, and would otherwise linger and resurface on the next change.
      await saveAndReschedule(
        { service_day_override: day || null, ...(day ? {} : { second_day_override: null }) },
        day ? `${client.name} moved to ${dayTitle(day)}s · visits rescheduled`
            : `${client.name} back on their zip's day${zipDay ? ` (${dayTitle(zipDay)}s)` : ""} · visits rescheduled`);
      setEditingDay(false);
    } catch (e) {
      console.error(e);
      showToast?.("Could not change the service day", "error");
    }
    setDayBusy(false);
  }
  // Which fortnight a biweekly client rides. Only meaningful when SOMETHING about them is
  // biweekly — a biweekly cleanup, or a biweekly deodorizer on a differently-paced cleanup
  // (Tracey Robinson is twice-weekly cleanup + biweekly deo). NULL means 'even', which is
  // what every client did before this existed.
  const isBiweekly = client.frequency === "biweekly" ||
                     (client.deo_enabled && client.deo_frequency === "biweekly");
  const [parityBusy, setParityBusy] = useState(false);

  async function saveParity(parity) {
    setParityBusy(true);
    try {
      // Changing the fortnight moves every upcoming visit by a week.
      await saveAndReschedule({ biweekly_parity: parity || null },
        `${client.name} set to ${parity === "odd" ? "odd" : "even"}-week fortnight · visits rescheduled`);
    } catch (e) {
      console.error(e);
      showToast?.("Could not change the fortnight", "error");
    }
    setParityBusy(false);
  }

  // TWICE-WEEKLY second day. Until now this could only be set per-ZIP, so a client whose
  // second visit didn't match their neighbours' had no way to be scheduled correctly —
  // and a twice-weekly client with no second day silently gets ONE visit a week while
  // paying for two.
  const isTwiceWeekly = client.frequency === "twice-weekly";
  const [secondDayBusy, setSecondDayBusy] = useState(false);

  async function saveSecondDay(day) {
    setSecondDayBusy(true);
    try {
      await saveAndReschedule({ second_day_override: day || null },
        day ? `${client.name}'s second visit moved to ${dayTitle(day)}s · visits rescheduled`
            : `${client.name} back on their zip's second day · visits rescheduled`);
    } catch (e) {
      console.error(e);
      showToast?.("Could not change the second service day", "error");
    }
    setSecondDayBusy(false);
  }

  // Dates carried over from the operator's previous system. These are what make a migrated
  // client keep their real rhythm instead of restarting on ours:
  //   • last cleanup  → derives the biweekly fortnight (that's the ONLY thing it drives)
  //   • last deo      → anchors a monthly deodorizer to their own cycle
  const [datesBusy, setDatesBusy] = useState(false);

  async function saveLastCleanup(dateStr) {
    setDatesBusy(true);
    try {
      const parity = dateStr ? parityFromDate(dateStr) : null;
      // Only touch the fortnight when this client actually HAS one, and never blank an
      // existing parity just because the date was cleared — that would silently re-guess.
      const patch = { last_service_date: dateStr || null };
      if (isBiweekly && parity) patch.biweekly_parity = parity;
      await saveAndReschedule(patch, dateStr
        ? `Last cleanup set to ${dateStr}${isBiweekly && parity ? ` · fortnight = ${parity} weeks` : ""} · visits rescheduled`
        : "Last cleanup date cleared");
    } catch (e) {
      console.error(e);
      showToast?.("Could not save the last cleanup date", "error");
    }
    setDatesBusy(false);
  }

  async function saveLastDeo(dateStr) {
    setDatesBusy(true);
    try {
      await saveAndReschedule({ deo_anchor_date: dateStr || null }, dateStr
        ? `Last deodorizer set to ${dateStr} · upcoming treatments rescheduled`
        : "Deodorizer anchor cleared — back to the calendar default");
    } catch (e) {
      console.error(e);
      showToast?.("Could not save the last deodorizer date", "error");
    }
    setDatesBusy(false);
  }

  // Status comes from the synced CLEANUP subscription (true Stripe state, incl. a
  // pause), falling back to client.status while it loads. This is what drives the
  // badge + Pause/Resume — so they reflect Stripe, not a stale CRM flag.
  const SUB_STATUS_MAP = { active:"active", trialing:"active", paused:"paused", past_due:"past_due", canceled:"deactivated", unpaid:"past_due", incomplete:"payment_pending", incomplete_expired:"deactivated" };
  const status       = (subscription?.status ? (SUB_STATUS_MAP[subscription.status] || subscription.status) : null) || client.status || "active";
  const cleanupBilling = status === "active" || status === "payment_pending";

  // Monthly = only the ACTIVE subs. A paused cleanup isn't billing, so it drops out
  // and the figure reflects just the active deodorizer (matches what Stripe charges).
  const deoMonthly   = (client.deo_enabled && client.deo_status === "active") ? getDeoMonthly(client.deo_frequency, pricing) : 0;
  const monthlyAmt   = (cleanupBilling ? getPrice(client.frequency, client.dogs, pricing) : 0) + deoMonthly;
  const pendingAmt   = client.pending_frequency ? (cleanupBilling ? getPrice(client.pending_frequency, client.pending_dogs||client.dogs, pricing) : 0) + deoMonthly : null;
  const paidInvoices = invoices.filter(i=>i.status==="paid");
  const totalSpent   = paidInvoices.reduce((s,i)=>s+(Number(i.amount)||0),0);

  // Load visit records when visits tab is opened.
  // The Visits tab is a history of ACTUAL visits — what happened — not
  // a preview of future scheduled jobs. Filter server-side to terminal
  // statuses (completed / skipped / missed) so the list shows only
  // visits that actually took place (or were explicitly not done).
  // Future "scheduled" + in-flight "started" rows live on the Schedule
  // tab; surfacing them here would conflate "what's coming" with "what
  // happened" and clutter the history.
  useEffect(()=>{
    if (tab !== "visits") return;
    setLoadingVisits(true);
    const statusFilter = "status=in.(completed,skipped,missed)";
    fetch(`${SUPABASE_URL}/rest/v1/jobs?client_id=eq.${client.id}&${statusFilter}&order=scheduled_date.desc&limit=200`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()).then(all=>{
      if (Array.isArray(all)) {
        // Jobs table IS the source of truth — use directly, no generation
        const byDate = {};
        all.forEach(v=>{ byDate[v.scheduled_date+(v.service_type||"cleanup")] = v; });
        setVisitRecords(byDate);
        setVisits(all); // use jobs directly
      }
      setLoadingVisits(false);
    }).catch(()=>setLoadingVisits(false));
    // The proof photo is ONE per job on job_headers.photo_url (the app uploads to
    // <header_id>/proof.jpg and records it via set_photo). Map it by date so the
    // Visits view shows it; fall back to a service photo_url for older/lifted photos.
    fetch(`${SUPABASE_URL}/rest/v1/job_headers?client_id=eq.${client.id}&select=scheduled_date,photo_url,duration_mins&order=scheduled_date.desc&limit=200`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()).then(hs=>{
      if (Array.isArray(hs)) {
        const m={}, d={};
        hs.forEach(h=>{ if (h.photo_url && !(h.scheduled_date in m)) m[h.scheduled_date]=h.photo_url; if (h.duration_mins!=null && !(h.scheduled_date in d)) d[h.scheduled_date]=h.duration_mins; });
        setHeaderPhotos(m); setHeaderDur(d);
      }
    }).catch(()=>{});
  },[tab, client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // GPS auto-tracked "typical clean" for this yard (prominent header stat + by-tech
  // breakdown). Reads the client_visit_times view; populates once the field app has
  // logged time_on_site visits. Independent of the Visits tab / job durations.
  const [visitStats, setVisitStats] = useState(null);
  useEffect(()=>{
    let alive = true;
    fetchClientVisits(client.id).then(rows=>{ if(alive) setVisitStats(summarizeClientVisits(rows, technicians||[])); });
    return ()=>{ alive = false; };
  },[client.id, technicians]);

  useEffect(()=>{
    setLoadingHist(true);
    fetch(`${SUPABASE_URL}/rest/v1/client_history?client_id=eq.${client.id}&order=created_at.desc&limit=100`,{
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
    }).then(r=>r.json()).then(data=>{
      if (Array.isArray(data)) setHistory(data);
      setLoadingHist(false);
    }).catch(()=>setLoadingHist(false));
  },[client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveContact() {
    setSaving(true);
    await onContactUpdate(client.id, contactForm, { name:client.name, email:client.email, phone:client.phone, phone_secondary:client.phone_secondary, address:client.address, zip:client.zip, referred_by:client.referred_by });
    setSaving(false);
    setEditContact(false);
  }

  // Record/withdraw texting consent right here (previously only capturable on the
  // public signup form → manually-added clients could never be texted). Writes
  // clients.sms_consent + a timestamp; send-eta / job-complete gate on this flag.
  async function toggleSmsConsent() {
    const next = !smsConsent;
    if (next && !window.confirm(`Confirm ${client.name} has opted in to text messages at ${client.phone||"their number"}? Only enable this if you have their consent.`)) return;
    setSaving(true);
    const res = await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${client.id}`, {
      method:"PATCH",
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
      body:JSON.stringify({ sms_consent: next, sms_consent_at: next ? new Date().toISOString() : null }),
    });
    setSaving(false);
    if (res.ok) { setSmsConsent(next); showToast(next ? "Texting consent recorded" : "Texting consent withdrawn"); }
    else showToast("Couldn't update texting consent","error");
  }

  // Reverse a mistaken skip-credit — posts a positive Stripe balance txn via
  // apply-credit (idempotent, atomically claims reversed_at) so it can't double-reverse.
  async function reverseCredit(credit) {
    if (!window.confirm(`Reverse the $${Number(credit.amount).toFixed(2)} credit issued ${credit.created_at?.slice(0,10)||""}? This charges it back to the client's balance and can't be undone.`)) return;
    setReversingId(credit.id);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/apply-credit`, {
        method:"POST",
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
        body:JSON.stringify({ action:"reverse", credit_id:credit.id, client_id:client.id }),
      });
      const body = await res.json().catch(()=>({}));
      if (res.ok && body.success) { showToast(`Credit reversed — $${Number(credit.amount).toFixed(2)} charged back`); onRefreshCredits && onRefreshCredits(); }
      else showToast(body.error || "Couldn't reverse the credit","error");
    } catch(e) { showToast(`Reverse failed: ${e.message||e}`,"error"); }
    setReversingId(null);
  }



  async function savePlan() {
    setSaving(true);
    if (planForm.frequency===client.frequency && Number(planForm.dogs)===Number(client.dogs)) {
      showToast("No changes to save","error");
      setSaving(false);
      setEditPlan(false);
      return;
    }
    await onScheduleChange(client.id, planForm.frequency, Number(planForm.dogs), client, planForm.deo_enabled, planForm.deo_frequency, pricing);
    if (onRefreshInvoices) onRefreshInvoices();
    setSaving(false);
    setEditPlan(false);
  }

  const EVENT_ICONS = {
  app_unlinked:"🔓", app_request:"📱", app_addon:"🧴", app_rating:"⭐", app_dog_name:"🐕",
  app_request_accepted:"✅", app_request_declined:"🚫", app_request_undone:"↩️",
    registered:"🎉", frequency_changed:"🔄", dogs_changed:"🐕", phone_changed:"📞",
    email_changed:"✉️", address_changed:"🏠", zip_changed:"📍", card_updated:"💳",
    paused:"⏸", resumed:"▶️", deactivated:"❌", reactivated:"✅",
    invoice_paid:"💰", subscription_updated:"🔁", name_changed:"👤", note_added:"📝", skipped_visit:"⊘", credit_issued:"🎁", invoice_sent:"📧", tech_assigned:"👤", manual_job:"⭐", payment_link_sent:"🔗", card_update_link_generated:"💳", bulk_imported:"📥",
  };

  const statusColor = status==="active"?"#006644":status==="paused"?"#974f0c":status==="payment_pending"?"#0747a6":"#42526e";
  const statusBg    = status==="active"?"#e3fcef":status==="paused"?"#fffae6":status==="payment_pending"?"#deebff":"#f4f5f7";

  // Phone layout helpers. Header actions become a 2-column tappable grid; the tiny
  // text-link / 10px buttons scattered through the tabs become real 36px targets.
  const hdrBtn  = isMobile ? {justifyContent:"center",fontSize:13} : undefined;
  const vBtn    = isMobile ? {fontSize:13} : {fontSize:10};
  const linkBtn = isMobile
    ? {background:"none",border:"1px solid var(--border)",borderRadius:8,padding:"7px 12px",minHeight:36,fontSize:13,cursor:"pointer"}
    : {background:"none",border:"none",padding:0,fontSize:11,cursor:"pointer"};

  return (
    <div style={ACTIVE_THEME === "apple-native" ? { maxWidth: 960, margin: "0 auto" } : undefined}>
      {/* ── Back link ── */}
      <div style={{marginBottom:12}}>
        <button className="btn btn-ghost btn-sm" onClick={onBack} style={{color:"#8993a4",fontSize:isMobile?13:12}}>
          ← Back to Clients
        </button>
      </div>

      {/* ── Header card ── */}
      <div className="card" style={{marginBottom:16}}>
        {/* Row 1: Avatar + Name + Actions */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:14,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:14,minWidth:0}}>
            <div style={{width:48,height:48,borderRadius:"50%",background:NAVY,color:GOLD,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:700,flexShrink:0}}>
              {(client.name||"?")[0].toUpperCase()}
            </div>
            <div style={{minWidth:0}}>
              <div style={{fontWeight:700,fontSize:18,color:"#1a1f36",letterSpacing:"-.3px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{client.name}</div>
              <div style={{fontSize:12,color:"#8993a4",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{client.email} · {client.phone}</div>
            </div>
          </div>
          <div style={isMobile
            ? {display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,width:"100%"}
            : {display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
            <button className="btn btn-ghost btn-sm" style={{...hdrBtn,fontSize:isMobile?13:11,opacity:!client.frequency?0.4:1,cursor:!client.frequency?"not-allowed":"pointer"}} onClick={()=>{if(!client.frequency){showToast("Set a service plan first","error");return;}setSendPaymentLink(true);}}>🔗 Payment Link</button>
            <button className="btn btn-ghost btn-sm" style={hdrBtn} disabled={generatingCardLink} onClick={generateCardUpdateLink}>{generatingCardLink?"Creating…":"📋 Copy card link"}</button>
            {client.stripe_customer_id&&<button className="btn btn-gold btn-sm" style={hdrBtn} onClick={()=>setShowCharge(true)}>💳 Charge</button>}
            <button className="btn btn-ghost btn-sm" style={hdrBtn} onClick={()=>onAddManualJob&&onAddManualJob(client)}>+ Job</button>
            {status==="active"     &&<button className="btn btn-ghost btn-sm" style={hdrBtn} onClick={()=>setShowPauseReason(true)}>⏸ Pause</button>}
            {status==="active"     &&<button className="btn btn-danger btn-sm" style={hdrBtn} onClick={()=>{ if(window.confirm(`Deactivate ${client.name}? This cancels their subscription and removes all upcoming visits. Any discount or custom price won't carry over if you reactivate later.`)) onStatusChange(client.id,"deactivate"); }}>Deactivate</button>}
            {status==="paused"     &&<button className="btn btn-gold btn-sm" style={hdrBtn} onClick={()=>onStatusChange(client.id,"resume")}>▶️ Resume</button>}
            {status==="paused"     &&<button className="btn btn-danger btn-sm" style={hdrBtn} onClick={()=>{ if(window.confirm(`Deactivate ${client.name}? This cancels their subscription and removes all upcoming visits. Any discount or custom price won't carry over if you reactivate later.`)) onStatusChange(client.id,"deactivate"); }}>Deactivate</button>}
            {status==="deactivated"&&<button className="btn btn-gold btn-sm" style={hdrBtn} onClick={()=>onStatusChange(client.id,"reactivate")}>Reactivate</button>}
          </div>
        </div>
        {/* Row 2: Status badges on their own line */}
        <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",paddingTop:10,borderTop:"1px solid #f0f2f5"}}>
          <span style={{background:statusBg,color:statusColor,padding:"2px 10px",borderRadius:20,fontSize:11,fontWeight:600}}>
            {status==="paused"?"⏸ Paused":status==="deactivated"?"Deactivated":status==="payment_pending"?"💳 Payment Pending":"● Active"}
            {client.needs_proration&&<span style={{fontSize:11,background:"#fef9c3",color:"#854d0e",padding:"2px 8px",borderRadius:4,marginLeft:6,fontWeight:600}}>💰 First payment pending</span>}
          </span>
          {serviceDay ? <span className="badge badge-blue" style={{textTransform:"capitalize"}}>{serviceDay}s</span> : <span className="badge badge-gray">No service day</span>}
          <span className="badge badge-gray" style={{textTransform:"capitalize"}}>🧹 {client.frequency}</span>
          {client.deo_enabled&&<span className="badge badge-blue">💨 Deodorizer ({client.deo_frequency==='weekly'?'Weekly':client.deo_frequency==='biweekly'?'Bi-Weekly':'Monthly'})</span>}
          <span className="badge badge-gray">{client.dogs} dog{client.dogs>1?"s":""}</span>
          {client.pending_frequency&&<span className="badge badge-gold">⏳ Change pending {client.pending_change_date}</span>}
          {client.plan_ends_on&&<span className="badge badge-red">Ends {client.plan_ends_on}</span>}
          {client.auth_user_id&&<span className="badge badge-blue" title="This client uses the customer app">📱 App login</span>}
                {client.needs_proration&&<span className="badge" style={{background:"#fffae6",color:"#974f0c",border:"1px solid #fde68a"}}>⚠️ Proration Pending</span>}
          {client.technician_name&&<span className="badge badge-blue">👤 {client.technician_name}</span>}
          <span className={`badge ${clientType==="commercial"?"badge-blue":"badge-gray"}`}>{clientType==="commercial"?"🏢 Commercial":"🏠 Residential"}</span>
        </div>
      </div>

      {/* ── Stats row ── (Typical Clean leads — the GPS-tracked headline metric) */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:(visitStats?.distinctTechs>1)?8:16}}>
        {[
          {label:"Typical Clean", value:visitStats?.avgMin!=null?`~${visitStats.avgMin} min`:"—", sub:visitStats?.visits?`${visitStats.visits} GPS visit${visitStats.visits>1?"s":""}`:"No visits yet", highlight:visitStats?.avgMin!=null, dim:visitStats!=null&&visitStats.avgMin==null},
          {label:"Monthly Rate",  value:`$${monthlyAmt.toFixed(2)}`, sub:client.frequency},
          {label:"Total Spent",   value:`$${totalSpent.toFixed(2)}`, sub:`${paidInvoices.length} paid invoices`},
          {label:"Client Since",  value:client.start_date||"—",      sub:"start date"},
          {label:"Next Bill",     value:pendingAmt?`$${pendingAmt.toFixed(2)}`:`$${monthlyAmt.toFixed(2)}`, sub:pendingAmt?"After pending change":"1st of next month"},
        ].map(s=>(
          <div key={s.label} className="stat-card" style={s.highlight?{boxShadow:`inset 0 0 0 1.5px ${GOLD}`}:s.dim?{opacity:.55}:undefined}>
            <div className="stat-accent"/>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{fontSize:18}}>{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>
      {/* By-tech clean-time breakdown — shown once more than one tech has serviced this yard */}
      {visitStats?.distinctTechs>1 && (
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:16}}>
          <span style={{fontSize:12,color:"#8993a4",fontWeight:600}}>🧹 Clean time by tech:</span>
          {visitStats.byTech.filter(b=>b.technician_id).map(b=>(
            <span key={b.technician_id} className="badge badge-gray">By: {b.technician} — ~{b.avgMin} min ({b.visits})</span>
          ))}
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="tabs">
        {[["overview","Overview"],["plan","Service Plan"],["billing","Billing"],["visits","Visits"],["history","History"]].map(([key,label])=>(
          <div key={key} className={`tab${tab===key?" active":""}`} onClick={()=>setTab(key)}>{label}</div>
        ))}
      </div>

      <div className="calm-pane">
      {/* ── OVERVIEW TAB ── */}
      {tab==="overview" && (
        <div className="grid-2">
          <div className="card">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div className="card-title" style={{marginBottom:0}}>Contact Information</div>
              <button className="btn btn-ghost btn-sm" onClick={()=>setEditContact(!editContact)}>
                {editContact?"Cancel":"✏️ Edit"}
              </button>
            </div>
            {editContact ? (
              <div>
                <div className="form-grid">
                  <div className="form-group full"><label>Full Name</label><input value={contactForm.name} onChange={e=>setContactForm(f=>({...f,name:e.target.value}))}/></div>
                  <div className="form-group"><label>Email</label><input type="email" value={contactForm.email} onChange={e=>setContactForm(f=>({...f,email:e.target.value}))}/></div>
                  <div className="form-group"><label>Phone</label><input value={contactForm.phone} onChange={e=>setContactForm(f=>({...f,phone:e.target.value}))}/></div>
                  {/* Second household number. ETA + "service complete" texts fan out to BOTH
                      (send-eta / send-job-complete dedupe, so the same number won't bill twice). */}
                  <div className="form-group">
                    <label>2nd Phone <span style={{fontWeight:400,color:"#8993a4",fontSize:11}}>— optional</span></label>
                    <input value={contactForm.phone_secondary} onChange={e=>setContactForm(f=>({...f,phone_secondary:e.target.value}))} placeholder="Also gets ETA + complete texts"/>
                  </div>
                  <div className="form-group"><label>Address</label><input value={contactForm.address} onChange={e=>setContactForm(f=>({...f,address:e.target.value}))}/></div>
                  <div className="form-group"><label>Zip Code</label><input value={contactForm.zip} onChange={e=>setContactForm(f=>({...f,zip:e.target.value}))}/></div>
                  <div className="form-group"><label>Referred By</label><input value={contactForm.referred_by||""} onChange={e=>setContactForm(f=>({...f,referred_by:e.target.value}))} placeholder="Name of referring client"/></div>
                </div>
                <div style={{display:"flex",gap:8,marginTop:12}}>
                  <button className="btn btn-gold" onClick={saveContact} disabled={saving}>{saving?"Saving...":"Save Changes"}</button>
                  <button className="btn btn-ghost" onClick={()=>setEditContact(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div>
                {[
                  {icon:"✉️", label:"Email",   val:client.email||"—"},
                  {icon:"📞", label:"Phone",   val:client.phone||"—"},
                  ...(client.phone_secondary ? [{icon:"📱", label:"2nd Phone", val:client.phone_secondary}] : []),
                  {icon:"🏠", label:"Address", val:client.address||"—"},
                  {icon:"📍", label:"Zip",     val:client.zip||"—"},
                  ...(client.referred_by ? [{icon:"🤝", label:"Referred By", val:client.referred_by}] : []),
                ].map(r=>(
                  <div key={r.label} style={{display:"flex",gap:10,padding:"7px 0",borderBottom:"1px solid #f4f5f7"}}>
                    <span style={{fontSize:14,width:20}}>{r.icon}</span>
                    <span style={{fontSize:12,color:"#8993a4",width:80,flexShrink:0}}>{r.label}</span>
                    <span style={{fontSize:13,fontWeight:500,color:"#253858",minWidth:0,wordBreak:"break-word"}}>{r.val}</span>
                  </div>
                ))}
                {/* Service day — normally derived from the zip, but overridable per client
                    for the occasional exception (a negotiated day, a boundary address). */}
                <div style={{display:"flex",gap:10,alignItems:"flex-start",padding:"7px 0",borderBottom:"1px solid #f4f5f7"}}>
                  <span style={{fontSize:14,width:20}}>📅</span>
                  <span style={{fontSize:12,color:"#8993a4",width:80,flexShrink:0,paddingTop:2}}>Service Day</span>
                  <div style={{minWidth:0,flex:1}}>
                    {!editingDay ? (
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span style={{fontSize:13,fontWeight:500,color:"#253858"}}>
                          {serviceDay ? dayTitle(serviceDay)+"s" : "Not assigned"}
                        </span>
                        {overridden && (
                          <span className="badge badge-gold" style={{fontSize:10}} title={zipDay ? `Zip ${client.zip} is ${dayTitle(zipDay)}s` : `Zip ${client.zip} has no service day`}>
                            custom
                          </span>
                        )}
                        {/* Touch has no title= tooltip: say what the zip default is inline on phones. */}
                        {overridden && isMobile && (
                          <span style={{fontSize:11,color:"#8993a4"}}>(zip default: {zipDay ? dayTitle(zipDay)+"s" : "none"})</span>
                        )}
                        <button onClick={()=>setEditingDay(true)} style={{...linkBtn,color:NAVY,textDecoration:isMobile?"none":"underline"}}>
                          change
                        </button>
                      </div>
                    ) : (
                      <div>
                        <select
                          value={client.service_day_override || ""}
                          disabled={dayBusy}
                          onChange={e=>saveDayOverride(e.target.value)}
                          style={{fontSize:12,width:"100%",maxWidth:230}}
                        >
                          <option value="">Use zip {client.zip}{zipDay ? ` — ${dayTitle(zipDay)}s` : " — no service day"}</option>
                          {DAYS.map(d=><option key={d} value={d}>{dayTitle(d)}s (just this client)</option>)}
                        </select>
                        <div style={{marginTop:6,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                          <button onClick={()=>setEditingDay(false)} disabled={dayBusy} style={{...linkBtn,color:"#8993a4"}}>
                            cancel
                          </button>
                          <span style={{fontSize:11,color:"#8993a4"}}>
                            {dayBusy ? "Rescheduling…" : "Moves only this client. Upcoming visits are rebuilt."}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Second service day — twice-weekly only. Defaults to the zip's second day;
                    this overrides it for one client. */}
                {isTwiceWeekly && (
                  <div style={{display:"flex",gap:10,alignItems:"flex-start",padding:"7px 0",borderBottom:"1px solid #f4f5f7"}}>
                    <span style={{fontSize:14,width:20}}>📆</span>
                    <span style={{fontSize:12,color:"#8993a4",width:80,flexShrink:0,paddingTop:2}}>Second Day</span>
                    <div style={{minWidth:0,flex:1}}>
                      <select
                        value={client.second_day_override || ""}
                        disabled={secondDayBusy}
                        onChange={e=>saveSecondDay(e.target.value)}
                        style={{fontSize:12,width:"100%",maxWidth:230}}
                      >
                        <option value="">Use zip {client.zip}{zipSecond ? ` — ${dayTitle(zipSecond)}s` : " — no second day set"}</option>
                        {DAYS.filter(d=>d!==serviceDay).map(d=><option key={d} value={d}>{dayTitle(d)}s (just this client)</option>)}
                      </select>
                      <div style={{marginTop:4,fontSize:11,color: (!client.second_day_override && !zipSecond) ? "#B54708" : "#8993a4"}}>
                        {secondDayBusy ? "Rescheduling…"
                          : (!client.second_day_override && !zipSecond)
                            ? "⚠ No second day — they're being billed twice a week but only scheduled once."
                            : "Their second visit each week. Can't be the same as the service day."}
                      </div>
                    </div>
                  </div>
                )}

                {/* Which fortnight — only shown when something about this client is biweekly. */}
                {isBiweekly && (
                  <div style={{display:"flex",gap:10,alignItems:"flex-start",padding:"7px 0",borderBottom:"1px solid #f4f5f7"}}>
                    <span style={{fontSize:14,width:20}}>🔁</span>
                    <span style={{fontSize:12,color:"#8993a4",width:80,flexShrink:0,paddingTop:2}}>Fortnight</span>
                    <div style={{minWidth:0,flex:1}}>
                      <select
                        value={client.biweekly_parity || "even"}
                        disabled={parityBusy}
                        onChange={e=>saveParity(e.target.value)}
                        style={{fontSize:12,width:"100%",maxWidth:230}}
                      >
                        <option value="even">Even weeks</option>
                        <option value="odd">Odd weeks</option>
                      </select>
                      <div style={{marginTop:4,fontSize:11,color:"#8993a4"}}>
                        {parityBusy ? "Rescheduling…"
                          : "Which of the two alternating weeks they're serviced on. Wrong = every visit a week off."}
                      </div>
                    </div>
                  </div>
                )}

                {/* Carried over from the operator's previous system. A migrated client has a
                    rhythm we can't infer from their start date, and guessing it is wrong 50%
                    of the time for a fortnight and 75% for a monthly deodorizer. One real
                    date each settles both. */}
                <div style={{display:"flex",gap:10,alignItems:"flex-start",padding:"7px 0",borderBottom:"1px solid #f4f5f7"}}>
                  <span style={{fontSize:14,width:20}}>⏮️</span>
                  <span style={{fontSize:12,color:"#8993a4",width:80,flexShrink:0,paddingTop:2}}>Last Cleanup</span>
                  <div style={{minWidth:0,flex:1}}>
                    <input
                      type="date"
                      value={client.last_service_date || ""}
                      disabled={datesBusy}
                      onChange={e=>saveLastCleanup(e.target.value)}
                      style={{fontSize:12,width:"100%",maxWidth:230}}
                    />
                    <div style={{marginTop:4,fontSize:11,color:"#8993a4"}}>
                      {datesBusy ? "Rescheduling…"
                        : isBiweekly
                          ? "Their last visit in your old system — sets the fortnight above automatically."
                          : "Their last visit in your old system. Reference only for this cadence."}
                    </div>
                  </div>
                </div>

                {client.deo_enabled && (
                  <div style={{display:"flex",gap:10,alignItems:"flex-start",padding:"7px 0",borderBottom:"1px solid #f4f5f7"}}>
                    <span style={{fontSize:14,width:20}}>💨</span>
                    <span style={{fontSize:12,color:"#8993a4",width:80,flexShrink:0,paddingTop:2}}>Last Deo</span>
                    <div style={{minWidth:0,flex:1}}>
                      <input
                        type="date"
                        value={client.deo_anchor_date || ""}
                        disabled={datesBusy}
                        onChange={e=>saveLastDeo(e.target.value)}
                        style={{fontSize:12,width:"100%",maxWidth:230}}
                      />
                      <div style={{marginTop:4,fontSize:11,color:"#8993a4"}}>
                        {datesBusy ? "Rescheduling…"
                          : client.deo_frequency === "monthly"
                            ? "Their last deodorizer. The next one lands about a month after this, on a day they're already being serviced."
                            : "Their last deodorizer. Updates itself each time one is completed."}
                      </div>
                    </div>
                  </div>
                )}

                {/* SMS/texting consent — capturable here, not just on the public signup form */}
                <div style={{display:"flex",gap:10,alignItems:"center",padding:"9px 0"}}>
                  <span style={{fontSize:14,width:20}}>💬</span>
                  <span style={{fontSize:12,color:"#8993a4",width:80,flexShrink:0}}>Texting</span>
                  <span style={{fontSize:13,fontWeight:500,color:smsConsent?"#1a7f4b":"#8993a4"}}>{smsConsent?"Consented":"No consent on file"}</span>
                  <button className="btn btn-ghost btn-sm" style={{marginLeft:"auto",fontSize:11}} disabled={saving} onClick={toggleSmsConsent}>{smsConsent?"Withdraw":"Mark consented"}</button>
                </div>
              </div>
            )}
          </div>

          <div className="card">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div className="card-title" style={{marginBottom:0}}>Technician Assignment</div>
            </div>
            {/* Out-of-area warning. This used to test the zip against a HARDCODED list of
                placeholder New York zips (10001-10023) that no real client has ever been in,
                so the condition was ALWAYS true and every client carried the warning — which
                trained the operator to ignore it. Now it fires on the real signal: the client
                has no resolvable service day at all (zip not in service_area AND no
                per-client override), which is exactly when they'd never be scheduled. */}
            {!serviceDay && (
              <div style={{background:"#ffebe6",border:"1px solid #ffbdad",borderRadius:6,padding:"8px 12px",marginBottom:12,fontSize:12,color:"#bf2600",lineHeight:1.5}}>
                ⚠️ <strong>No service day</strong> — zip code {client.zip} isn't in your service area, so no visits will be scheduled even with a technician assigned.
                Add {client.zip} under Settings → Team &amp; Service → Service area, or set a day for just this client using <strong>change</strong> next to Service Day above.
              </div>
            )}
            <div className="form-group">
              <label>Assigned Technician</label>
              {ACTIVE_THEME === "apple-native" ? (
                <div style={{display:"flex",flexWrap:"wrap",gap:10,marginTop:4}}>
                  {[{id:"",name:"Unassigned"},...(technicians||[]).filter(t=>t.active)].map(t=>{
                    const sel = selectedTech === t.id;
                    const none = t.id === "";
                    const ini = none ? "—" : (t.name||"?").trim().split(/\s+/).slice(0,2).map(w=>w[0]||"").join("").toUpperCase();
                    return (
                      <div key={t.id||"none"} onClick={()=>!assigningTech&&setSelectedTech(t.id)}
                        style={{
                          display:"flex",alignItems:"center",gap:11,padding:"9px 16px 9px 9px",borderRadius:14,cursor:"pointer",minWidth:isMobile?"calc(50% - 5px)":158,boxSizing:isMobile?"border-box":undefined,
                          border: sel?"1.5px solid var(--accent)":"1px solid var(--border)",
                          background: sel?"var(--accent-weak)":"var(--surface)",
                          boxShadow: sel?"0 8px 20px -7px rgba(0,113,227,.4)":"none",
                          transform: sel?"translateY(-1px)":"none", transition:".16s var(--ease)",
                        }}>
                        <div style={{width:38,height:38,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:600,fontSize:14,flexShrink:0,
                          background: sel?"linear-gradient(150deg, #0B3DA8 0%, #0A84FF 100%)":"rgba(118,118,128,.14)", color: sel?"#fff":"var(--text2)"}}>{ini}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13.5,fontWeight:600,color:"var(--text)"}}>{t.name}</div>
                          <div style={{fontSize:11,color:"var(--text2)"}}>{none?"no technician":(t.hourly_rate?`$${t.hourly_rate}/hr`:"technician")}</div>
                        </div>
                        {sel && <span style={{color:"var(--accent)",fontSize:16,fontWeight:700}}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              ) : (
              <select
                value={selectedTech}
                disabled={assigningTech}
                onChange={e=>setSelectedTech(e.target.value)}
              >
                <option value="">— Unassigned —</option>
                {(technicians||[]).filter(t=>t.active).map(t=>(
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              )}

              {/* Stage-and-Apply: nothing hits the backend until the
                  Apply button is clicked. Gives the user an explicit
                  "I'm committing this change now" moment and lets them
                  see what's about to happen (cascade to future visits). */}
              {selectedTech !== (client.technician_id || "") && (
                <div style={{marginTop:10,background:"#fffae6",border:"1px solid #fde047",borderRadius:6,padding:"10px 12px"}}>
                  <div style={{fontSize:12,color:"#854d0e",marginBottom:8,lineHeight:1.5}}>
                    <strong>Pending change:</strong>{" "}
                    {client.technician_name || "Unassigned"}
                    {" → "}
                    <strong>{technicians?.find(t=>t.id===selectedTech)?.name || "Unassigned"}</strong>
                    <br/>
                    <span style={{fontSize:11,color:"#8993a4"}}>
                      This will also reassign all future scheduled visits for {client.name}. Past + completed visits stay with the original tech.
                    </span>
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <button
                      className="btn btn-gold btn-sm"
                      disabled={assigningTech}
                      onClick={async()=>{
                        const val  = selectedTech;
                        const tech = technicians?.find(t=>t.id===val);
                        setAssigningTech(true);
                        await onAssignTech(client.id, val||null, tech?.name||null);
                        setAssigningTech(false);
                      }}
                    >
                      {assigningTech ? "Applying..." : "✓ Apply Assignment"}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      disabled={assigningTech}
                      onClick={()=>setSelectedTech(client.technician_id || "")}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            {ACTIVE_THEME !== "apple-native" && selectedTech && technicians?.find(t=>t.id===selectedTech) && (
              <div style={{marginTop:8,fontSize:12,color:"#006644",background:"#e3fcef",padding:"6px 10px",borderRadius:6}}>
                ✓ Assigned to <strong>{technicians.find(t=>t.id===selectedTech)?.name}</strong>
              </div>
            )}
          </div>

          <div className="card" style={{gridColumn:"1/-1"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div className="card-title" style={{marginBottom:0}}>Client Type</div>
            </div>
            <div style={{display:"flex",gap:10}}>
              {[{val:"residential",label:"🏠 Residential"},{val:"commercial",label:"🏢 Commercial"}].map(({val,label})=>(
                <button key={val} disabled={savingType}
                  onClick={async()=>{
                    setSavingType(true);
                    setClientType(val);
                    await fetch(`${SUPABASE_URL}/rest/v1/clients?id=eq.${client.id}`,{
                      method:"PATCH",
                      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
                      body:JSON.stringify({client_type:val}),
                    });
                    await fetch(`${SUPABASE_URL}/rest/v1/client_history`,{
                      method:"POST",
                      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
                      body:JSON.stringify({client_id:client.id,event_type:"type_changed",description:`Client type set to ${val}`,new_value:val}),
                    });
                    setSavingType(false);
                  }}
                  style={{flex:1,padding:"12px",borderRadius:8,border:`2px solid ${clientType===val?NAVY:"#e3e8ef"}`,background:clientType===val?NAVY:WHITE,color:clientType===val?WHITE:"#42526e",cursor:"pointer",fontFamily:"Inter,sans-serif",fontSize:13,fontWeight:clientType===val?700:500,transition:".15s"}}>
                  {label}
                </button>
              ))}
            </div>
            {savingType&&<div style={{fontSize:11,color:"#8993a4",marginTop:6}}>Saving...</div>}
          </div>

          {/* Client Notes + Visit Notes */}
          <div className="card" style={{gridColumn:"1/-1"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div className="card-title" style={{marginBottom:0}}>Notes</div>
              <button className="btn btn-gold btn-sm" onClick={saveNotes} disabled={savingNotes}>{savingNotes?"Saving...":"Save Notes"}</button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label style={{display:"flex",alignItems:"center",gap:6}}>
                  📋 <strong>Client Notes</strong>
                  <span style={{fontSize:11,color:"#8993a4",fontWeight:400}}>— visible on profile only</span>
                </label>
                <textarea value={clientNotes} onChange={e=>setClientNotes(e.target.value)} placeholder="Gate code, dog names, special instructions, billing notes..." style={{minHeight:80}}/>
              </div>
              <div className="form-group">
                <label style={{display:"flex",alignItems:"center",gap:6}}>
                  🔁 <strong>Visit Notes</strong>
                  <span style={{fontSize:11,color:"#8993a4",fontWeight:400}}>— shown on every job for this client</span>
                </label>
                <textarea value={visitNotes} onChange={e=>setVisitNotes(e.target.value)} placeholder="e.g. Check back gate. Dog is friendly but jumps. Leave gate closed." style={{minHeight:80}}/>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Stripe Info</div>
            {[
              {icon:"🔑", label:"Customer ID",    val:client.stripe_customer_id||"—"},
              {icon:"💳", label:"Card on File",   val:client.stripe_pm_last4?"**** **** **** "+client.stripe_pm_last4+" ("+client.stripe_pm_brand+")":"No card"},
              {icon:"🔄", label:"Sub Status",     val:subscription?.status||"—", capitalize:true},
              {icon:"💰", label:"Monthly Amount", val:subscription?.amount?`$${Number(subscription.amount).toFixed(2)}`:"—"},
              {icon:"📅", label:"Next Billing",   val:subscription?.next_billing_date?new Date(subscription.next_billing_date).toLocaleDateString():"—"},
              {icon:"🎟️", label:"Discount",       val:subscription?.discount_name||"None"},
            ].map(r=>(
              <div key={r.label} style={{display:"flex",gap:10,padding:"7px 0",borderBottom:"1px solid #f4f5f7"}}>
                <span style={{fontSize:14,width:20}}>{r.icon}</span>
                <span style={{fontSize:12,color:"#8993a4",width:100,flexShrink:0}}>{r.label}</span>
                <span style={{fontSize:11,fontWeight:500,color:"#253858",wordBreak:"break-all"}}>{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── PLAN TAB ── */}
      {tab==="plan" && (
        <div className="grid-2">
          <div className="card">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div className="card-title" style={{marginBottom:0}}>Service Plan</div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                {loadingSub&&<span style={{fontSize:11,color:"#8993a4"}}>⟳ Syncing from Stripe...</span>}
                {(status==="active"||status==="payment_pending")&&<button className="btn btn-ghost btn-sm" onClick={()=>setEditPlan(!editPlan)}>{editPlan?"Cancel":"✏️ Edit Plan"}</button>}
              </div>
            </div>

            {client.pending_frequency&&(
              <div style={{background:"#fffae6",border:"1px solid #fde047",borderRadius:6,padding:"10px 12px",marginBottom:14,fontSize:12,color:"#974f0c"}}>
                ⏳ <strong>Change scheduled for {client.pending_change_date}:</strong> {client.pending_frequency}, {client.pending_dogs} dog{client.pending_dogs>1?"s":""} → ${getPrice(client.pending_frequency,client.pending_dogs,pricing).toFixed(2)}/mo
              </div>
            )}

            {editPlan ? (
              <div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Cleanup Frequency</label>
                    <select value={planForm.frequency} onChange={e=>setPlanForm(f=>({...f,frequency:e.target.value}))}>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-Weekly</option>
                      <option value="twice-weekly">Twice a Week</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Number of Dogs</label>
                    <select value={planForm.dogs} onChange={e=>setPlanForm(f=>({...f,dogs:Number(e.target.value)}))}>
                      {[1,2,3,4].map(n=><option key={n} value={n}>{n} dog{n>1?"s":""}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{background:"#f4f5f7",borderRadius:6,padding:"10px 14px",marginTop:12,fontSize:12,color:"#42526e"}}>
                  <div>Cleanup: <strong>${getPrice(client.frequency,client.dogs,pricing).toFixed(2)}/mo</strong> → <strong style={{color:NAVY}}>${getPrice(planForm.frequency,planForm.dogs,pricing).toFixed(2)}/mo</strong></div>
                  <div style={{marginTop:4,color:"#8993a4"}}>Change takes effect on the 1st of next month. Current billing cycle unchanged. Deodorizer is managed separately below.</div>
                </div>
                <div style={{display:"flex",gap:8,marginTop:12}}>
                  <button className="btn btn-gold" onClick={savePlan} disabled={saving}>{saving?"Saving...":status==="payment_pending"?"Save Plan":"Schedule Change"}</button>
                  <button className="btn btn-ghost" onClick={()=>setEditPlan(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div>
                {status==="payment_pending" && !client.frequency && (
                  <div style={{background:"#f4f5f7",borderRadius:8,padding:"16px",textAlign:"center",color:"#8993a4",fontSize:13}}>
                    No service plan set yet.<br/>
                    <span style={{fontSize:12}}>Click <strong>✏️ Edit Plan</strong> above to set frequency, dogs, and deodorizer.</span>
                  </div>
                )}
                {(status!=="payment_pending" || client.frequency) && [{
                  label:"Cleanup Freq", val:loadingSub?"Loading...":(subscription?.frequency||client.frequency||"Not set"), capitalize:true},
                  {label:"Dogs",         val:loadingSub?"Loading...":(subscription?.dogs||client.dogs)?(((subscription?.dogs||client.dogs))+" dog"+((subscription?.dogs||client.dogs)>1?"s":"")):"Not set"},
                  {label:"Monthly Rate", val:subscription?.amount?`$${Number(subscription.amount).toFixed(2)}/month`:client.frequency?`$${monthlyAmt.toFixed(2)}/month`:"—"},
                  {label:"Sub Status",   val:subscription?.status||client.status||"—", capitalize:true},
                  {label:"Next Billing", val:subscription?.next_billing_date?new Date(subscription.next_billing_date).toLocaleDateString():"—"},
                  {label:"Billing Cycle",val:subscription?.current_period_start&&subscription?.current_period_end?`${new Date(subscription.current_period_start).toLocaleDateString()} → ${new Date(subscription.current_period_end).toLocaleDateString()}`:"—"},
                  ...(subscription?.discount_name?[{label:"Discount", val:`${subscription.discount_name}${subscription.discount_amount?` (-$${Number(subscription.discount_amount).toFixed(2)})`:`${subscription.discount_percent?` (-${subscription.discount_percent}%)`:""}`}`}]:[]),
                  ...(status==="payment_pending"&&client.frequency?[{label:"First Charge", val:"Prorated — based on remaining visits this month"}]:[]),
                  {label:"Service Day",  val:serviceDay ? serviceDay.charAt(0).toUpperCase()+serviceDay.slice(1)+"s" : "Not assigned"},
                ].map(r=>(
                  <div key={r.label} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:"1px solid #f4f5f7"}}>
                    <span style={{fontSize:12,color:"#8993a4",width:110,flexShrink:0}}>{r.label}</span>
                    <span style={{fontSize:13,fontWeight:600,color:"#253858",textTransform:r.capitalize?"capitalize":"none"}}>{r.val}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── DEODORIZER CARD ── State-aware lifecycle UI ─────────────────
              Three states based on client.deo_status:
                null/cancelled → "+ Add Deodorizer" button
                active         → freq display + Pause + Cancel + Change Freq
                paused         → resume banner + Resume + Cancel buttons         */}
          <DeodorizerCard client={client} pricing={pricing} onDeoAction={onDeoAction} />

          <div className="card">
            <div className="card-title">Recent Invoices</div>
            {invoices.slice(0,5).map(inv=>(
              <div key={inv.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid #f4f5f7"}}>
                <div>
                  <div style={{fontSize:12,fontWeight:600}}>{inv.invoice_number}</div>
                  <div style={{fontSize:11,color:"#8993a4"}}>{inv.date}</div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontWeight:700,fontSize:13,color:NAVY}}>${Number(inv.amount).toFixed(2)}</span>
                  <span className={`badge badge-${inv.status==="paid"?"green":inv.status==="open"?"gold":"red"}`} style={{textTransform:"capitalize"}}>{inv.status}</span>
                </div>
              </div>
            ))}
            {invoices.length===0&&<div className="empty"><p>No invoices yet</p></div>}
          </div>
        </div>
      )}

      {/* ── BILLING TAB ── */}
      {tab==="billing" && (
        <>
        <ClientBillingTab
          invoices={invoices}
          client={client}
          onApprove={onApprove}
          onRetryCharge={onRetryCharge}
          onIssueRefund={onIssueRefund}
          onVoidInvoice={onVoidInvoice}
          onEdit={onEditInvoice}
          onRefreshInvoices={onRefreshInvoices}
          showToast={showToast}
        />
        {clientCredits.length>0 && (
          <div className="card" style={{marginTop:12}}>
            <div className="card-title" style={{marginBottom:6}}>Account Credits</div>
            <div style={{fontSize:12,color:"#8993a4",marginBottom:10}}>Auto-applied to the next invoice. Reverse one issued by mistake — it charges the amount back to the client's balance.</div>
            {clientCredits.map(c=>(
              <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid #f4f5f7"}}>
                <span style={{fontFamily:"Syne",fontWeight:700,color:"#1a7f4b",fontSize:14,minWidth:64}}>-${Number(c.amount).toFixed(2)}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,color:"#253858"}}>{c.description||"Credit"}</div>
                  <div style={{fontSize:11,color:"#8993a4"}}>{c.created_at?.slice(0,10)}</div>
                </div>
                <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:"#bf2600"}} disabled={reversingId===c.id} onClick={()=>reverseCredit(c)}>{reversingId===c.id?"Reversing…":"↩ Reverse"}</button>
              </div>
            ))}
          </div>
        )}
        </>
      )}

      {/* ── VISITS TAB ── */}
      {tab==="visits" && (
        <div>
          {/* Summary bar */}
          <div className="card" style={{marginBottom:14,padding:"12px 16px"}}>
            <div className="flex justify-between items-center" style={{flexWrap:"wrap",gap:8}}>
              <div style={{display:"flex",gap:16,fontSize:13,flexWrap:"wrap"}}>
                <span><strong>{visits.length}</strong> <span style={{color:"#8993a4"}}>total</span></span>
                <span style={{color:"#006644"}}><strong>{visits.filter(v=>v.status==="completed").length}</strong> completed</span>
                <span style={{color:"#974f0c"}}><strong>{visits.filter(v=>v.status==="skipped").length}</strong> skipped</span>
                <span style={{color:"#bf2600"}}><strong>{visits.filter(v=>v.status==="missed").length}</strong> missed</span>
              </div>
              <div style={{fontSize:11,color:"#8993a4"}}>Most recent first</div>
            </div>
          </div>

          {loadingVisits && <div style={{padding:20,textAlign:"center",color:"#8993a4",fontSize:13}}>Loading visits...</div>}
          {!loadingVisits && visits.length===0 && <div className="empty"><p>No past visits found for this client</p></div>}

          {!loadingVisits && visits.length>0 && (
            <div className="card" style={{padding:0,overflow:"hidden"}}>
              {/* A client's whole day = ONE visit. The jobs table keeps cleanup
                  and deodorizer as separate rows; here we group this client's
                  terminal jobs by scheduled_date and render nested, individually
                  clickable service line-items — the same "one client/day = one
                  card" model the field app and the Schedule day-view use. */}
              {(()=>{
                const order = [];
                const byDay = new Map();
                visits.forEach(v=>{
                  const k = v.scheduled_date || ("row-"+(v.id||""));
                  if (!byDay.has(k)) { byDay.set(k, []); order.push(k); }
                  byDay.get(k).push(v);
                });
                return order.map((day, gi)=>{
                  // cleanup before deodorizer for a stable, readable order
                  const group = byDay.get(day).slice().sort((a,b)=>
                    (((a.service_type||"cleanup")==="deo")?1:0) - (((b.service_type||"cleanup")==="deo")?1:0));

                  // Visit "happened" at the LAST service finished — max non-null completed_at.
                  const doneTimes = group.map(j=>j.completed_at).filter(Boolean)
                    .map(t=>new Date(t).getTime()).filter(n=>!isNaN(n));
                  const lastDone  = doneTimes.length ? new Date(Math.max(...doneTimes)) : null;
                  const dateMain  = lastDone
                    ? lastDone.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})
                    : day;
                  const dateSub   = lastDone && lastDone.toISOString().split("T")[0] !== day
                    ? `scheduled ${day}` : null;

                  // One proof photo for the whole visit: prefer the header's photo (set by
                  // the app via set_photo), else a service photo_url (cleanup first).
                  const photoJob  = group.find(j=>(j.service_type||"cleanup")!=="deo" && j.photo_url)
                                 || group.find(j=>j.photo_url);
                  const photoPath = headerPhotos[day] || photoJob?.photo_url || null;

                  // Visit-level status: all done → Completed; all skipped/missed → that;
                  // any mix → Partial (the per-service badges below say which is which).
                  const ss = group.map(j=>j.status);
                  const visitBadge = ss.every(s=>s==="completed")      ? <span className="badge badge-green">✓ Completed</span>
                    : ss.every(s=>s==="skipped")                       ? <span className="badge badge-red">⊘ Skipped</span>
                    : ss.every(s=>s==="missed")                        ? <span className="badge badge-gray">❌ Missed</span>
                    : ss.every(s=>s==="skipped"||s==="missed")         ? <span className="badge badge-gray">⊘ Not done</span>
                    :                                                    <span className="badge badge-gold">◑ Partial</span>;

                  return (
                    <div key={day} style={{padding:"14px 16px",borderBottom: gi<order.length-1?"1px solid #f0f2f5":"none"}}>
                      {/* Visit header — when · status · finished-time · the one proof photo */}
                      <div style={{display:"flex",alignItems:"center",gap:12}}>
                        <div style={{flex:1,minWidth:0,cursor:group[0]?.id?"pointer":"default"}}
                          title={group[0]?.id?"Open this job":undefined}
                          onClick={()=>{ if(group[0]?.id) setJobDetailId(group[0].id); }}>
                          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                            <span style={{fontWeight:700,fontSize:14,color:"#1a1f36"}}>{dateMain}</span>
                            {visitBadge}
                            {group.length>1 && <span style={{fontSize:11,color:"#8993a4"}}>{group.length} services</span>}
                          </div>
                          {dateSub && <div style={{fontSize:10,color:"#8993a4",marginTop:2}}>{dateSub}</div>}
                          <div style={{fontSize:11,color:"#8993a4",marginTop:2}}>
                            {lastDone ? `Finished ${lastDone.toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}` : "Not completed"}
                            {headerDur[day]!=null && <> · ⏱ {headerDur[day]>=60?`${Math.floor(headerDur[day]/60)}h ${headerDur[day]%60}m`:`${headerDur[day]}m`} total</>}
                          </div>
                        </div>
                        <div style={{flexShrink:0}}>
                          {photoPath ? <JobPhoto path={photoPath}/> : <span style={{color:"#b3bac5",fontSize:12}}>—</span>}
                        </div>
                      </div>

                      {/* Nested service line-items — each opens its own job modal and keeps
                          its own status, time, technician reassign and actions. */}
                      <div style={{marginTop:10,paddingLeft: group.length>1?12:0,borderLeft: group.length>1?"2px solid #e3e8ef":"none",display:"flex",flexDirection:"column",gap:6}}>
                        {group.map((v,i)=>{
                          // Whole line-item opens the job modal; clicks on the reassign
                          // control or action buttons are ignored so they keep working.
                          const onRowClick = (e) => {
                            if (e.target.closest("button") || e.target.closest("select")) return;
                            if (!v.id) return; // synthetic row, nothing to open
                            setJobDetailId(v.id);
                          };
                          return (
                          <div key={v.id||(day+"-"+i)} onClick={onRowClick}
                            style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",cursor:v.id?"pointer":"default",
                              padding:"6px 8px",borderRadius:6,background:v.status==="completed"?"#f0fdf4":(v.status==="skipped"||v.status==="missed")?"#fafafa":"#fff"}}>
                            {/* service label + per-service status + per-service time */}
                            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",flex:1,minWidth:isMobile?0:200,flexBasis:isMobile?"100%":undefined}}>
                              <span style={{fontSize:13,fontWeight:600,color:"#1a1f36",textDecoration:(v.status==="skipped"||v.status==="missed")?"line-through":"none"}}>
                                {v.service_type==="deo"?"💨 Deodorizer":"🧹 Cleanup"}
                              </span>
                              {v.status==="completed"&&<span className="badge badge-green" style={{fontSize:10}}>✓ Completed</span>}
                              {v.status==="skipped"  &&<span className="badge badge-red" style={{fontSize:10}}>⊘ Skipped</span>}
                              {v.status==="missed"   &&<span className="badge badge-gray" style={{fontSize:10}}>❌ Missed</span>}
                              {v.satisfaction_rating&&<span style={{fontSize:11}}>{"⭐".repeat(v.satisfaction_rating)}</span>}
                              {v.credited&&<span style={{fontSize:10,color:"#006644",background:"#e3fcef",padding:"1px 6px",borderRadius:4}}>💰 Credited</span>}
                              {v.completed_at&&<span style={{fontSize:11,color:"#8993a4"}}>{new Date(v.completed_at).toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}</span>}
                            </div>
                            {/* per-service technician reassign (PRESERVED, keyed per service) */}
                            <div style={{minWidth:isMobile?"auto":150}}>
                              {visitReassign===v.scheduled_date+(v.service_type||"") ? (
                                <select style={{fontSize:12,padding:isMobile?"8px 10px":"3px 6px",minHeight:isMobile?40:undefined,borderRadius:4,border:"1px solid #dfe1e6"}}
                                  defaultValue={v.technician_id||""}
                                  onChange={async e=>{
                                    const tech = (technicians||[]).find(t=>t.id===e.target.value);
                                    const upsertData = {
                                      client_id:      client.id,
                                      scheduled_date: v.scheduled_date,
                                      status:         v.status||"completed",
                                      service_type:   v.service_type||"cleanup",
                                      technician_id:  e.target.value||null,
                                      technician_name:tech?.name||null,
                                    };
                                    if (v.id) {
                                      await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${v.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({technician_id:e.target.value||null,technician_name:tech?.name||null})});
                                    } else {
                                      await fetch(`${SUPABASE_URL}/rest/v1/jobs`,{method:"POST",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify(upsertData)});
                                    }
                                    setVisitReassign(null);
                                    const all = await fetch(`${SUPABASE_URL}/rest/v1/jobs?client_id=eq.${client.id}&order=scheduled_date.desc&limit=200`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
                                    if (Array.isArray(all)) { const mine=all.filter(r=>r.client_id===client.id); const byDate={}; mine.forEach(r=>{byDate[r.scheduled_date+(r.service_type||"cleanup")]=r;}); setVisitRecords(byDate); setVisits(mine); }
                                  }}>
                                  <option value="">— Unassigned —</option>
                                  {(technicians||[]).filter(t=>t.active).map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                              ) : (
                                <span style={{fontSize:12,cursor:"pointer",color:v.technician_name?NAVY:"#b3bac5",...(isMobile?{display:"inline-flex",alignItems:"center",minHeight:36,padding:"0 4px"}:{})}} onClick={(e)=>{e.stopPropagation();setVisitReassign(v.scheduled_date+(v.service_type||""));}}>
                                  👤 {v.technician_name||"Unassigned"} <span style={{fontSize:10,color:"#b3bac5"}}>✏️</span>
                                </span>
                              )}
                            </div>
                            {/* per-service notes */}
                            {/* On a phone the note wraps in full under the row (no hover tooltip to fall back on). */}
                            {(v.skipped_reason||v.notes)&&(()=>{ const skipTxt = v.skipped_reason ? skipReasonLabel(v.skipped_reason)+(v.skip_note?` — ${v.skip_note}`:"") : v.notes; return <span style={isMobile
                              ? {fontSize:12,color:"#8993a4",flexBasis:"100%",whiteSpace:"normal",wordBreak:"break-word"}
                              : {fontSize:11,color:"#8993a4",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={isMobile?undefined:skipTxt}>{skipTxt}</span>; })()}
                            {/* per-service actions (PRESERVED) */}
                            <div className="flex gap-2" style={isMobile?{marginLeft:"auto",flexWrap:"wrap"}:undefined}>
                              {v.status!=="completed"&&(
                                <button className="btn btn-ghost btn-sm" style={vBtn} onClick={async()=>{
                                  const completedNow = new Date().toISOString();
                                  const tech = (technicians||[]).find(t=>t.id===client.technician_id);
                                  if (v.id) {
                                    await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${v.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({status:"completed",skipped_reason:null,skip_note:null,credited:false,completed_at:completedNow})});
                                  } else {
                                    await fetch(`${SUPABASE_URL}/rest/v1/jobs`,{method:"POST",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({client_id:client.id,client_name:client.name,scheduled_date:v.scheduled_date,service_type:v.service_type||"cleanup",status:"completed",completed_at:completedNow,technician_id:client.technician_id||null,technician_name:tech?.name||null,source:"manual"})});
                                  }
                                  const all = await fetch(`${SUPABASE_URL}/rest/v1/jobs?client_id=eq.${client.id}&order=scheduled_date.desc&limit=200`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
                                  if (Array.isArray(all)) { const mine=all.filter(r=>r.client_id===client.id); const byDate={}; mine.forEach(r=>{byDate[r.scheduled_date+(r.service_type||"cleanup")]=r;}); setVisitRecords(byDate); setVisits(mine); }
                                }}>✓ Complete</button>
                              )}
                              {v.status==="completed"&&(
                                <button className="btn btn-ghost btn-sm" style={{...vBtn,color:"#0747a6"}} onClick={()=>setSkipModal({...v, _mode:"credit"})}>🎁 Issue Credit</button>
                              )}
                              {v.status!=="missed"&&v.status!=="completed"&&(
                                <button className="btn btn-ghost btn-sm" style={{...vBtn,color:"#bf2600"}} onClick={async()=>{
                                  if (v.id) {
                                    await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${v.id}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({status:"missed"})});
                                  } else {
                                    await fetch(`${SUPABASE_URL}/rest/v1/jobs`,{method:"POST",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({client_id:client.id,client_name:client.name,scheduled_date:v.scheduled_date,service_type:v.service_type||"cleanup",status:"missed",source:"manual"})});
                                  }
                                  const all = await fetch(`${SUPABASE_URL}/rest/v1/jobs?client_id=eq.${client.id}&order=scheduled_date.desc&limit=200`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
                                  if (Array.isArray(all)) { const mine=all.filter(r=>r.client_id===client.id); const byDate={}; mine.forEach(r=>{byDate[r.scheduled_date+(r.service_type||"cleanup")]=r;}); setVisitRecords(byDate); setVisits(mine); }
                                }}>❌ Missed</button>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}

          {/* Skip Visit Modal — also handles the "issue credit on a
              completed visit" flow when opened with _mode:"credit".
              In credit mode the visit stays completed and we only
              flip the `credited` flag (no status change). */}
          {skipModal && (
            <SkipVisitModal
              visit={skipModal}
              client={client}
              pricing={pricing}
              mode={skipModal._mode || "skip"}
              onClose={()=>setSkipModal(null)}
              onSave={async(reason, credit, skipNote)=>{
                const isCreditMode = skipModal._mode === "credit";
                const svcType   = skipModal.service_type || skipModal.serviceType || "cleanup";
                const schedDate = skipModal.scheduled_date || skipModal.date;
                // 1. Ensure the job row exists and capture its id, so the credit can be
                //    keyed on a REAL job_id (apply-credit's `credit:apply:${job_id}`
                //    idempotency). Do NOT mark credited yet — only after the credit posts,
                //    so a Stripe failure never leaves a false credited=true.
                let jobId = skipModal.id || null;
                if (jobId) {
                  const patchBody = isCreditMode ? { notes: reason } : { status: "skipped", skipped_reason: reason, skip_note: skipNote || null };
                  await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${jobId}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify(patchBody)});
                } else {
                  const insertBody = { client_id: client.id, client_name: client.name, scheduled_date: schedDate, service_type: svcType, status: isCreditMode ? "completed" : "skipped", credited: false, ...(isCreditMode ? { notes: reason } : { skipped_reason: reason, skip_note: skipNote || null }), technician_id: client.technician_id||null, technician_name: client.technician_name||null, source: "manual" };
                  const rows = await fetch(`${SUPABASE_URL}/rest/v1/jobs`,{method:"POST",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=representation"},body:JSON.stringify(insertBody)}).then(r=>r.json()).catch(()=>null);
                  jobId = Array.isArray(rows) ? rows[0]?.id : rows?.id;
                }
                // 2. Post the credit on the real job_id; mark credited ONLY on success.
                let creditApplied = false;
                if (credit) {
                  // B10: credit the rate for the actual service (deo vs cleanup).
                  const dFreq    = client.deo_frequency || "biweekly";
                  const perVisit = svcType === "deo"
                    ? (pricing?.deoPerVisit?.[dFreq] ?? DEO_PER_VISIT[dFreq] ?? 0)
                    : getPerVisit(client.frequency,client.dogs,pricing);
                  if (!jobId || !pricing || !(perVisit > 0)) {
                    // No real job_id (the row write failed) or no price → never post a
                    // non-idempotent / guessed credit. The visit stands; warn to retry.
                    showToast("Saved, but credit NOT applied — couldn't record the visit or price it. Retry the credit from the profile.","error");
                  } else {
                    try {
                      const res = await fetch(`${SUPABASE_URL}/functions/v1/apply-credit`,{
                        method:"POST",
                        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json"},
                        body:JSON.stringify({ action:"apply", client_id: client.id, amount: perVisit,
                          description:`${isCreditMode?"Goodwill":"Skipped visit"} credit — ${schedDate}${reason?` (${isCreditMode ? reason : skipReasonLabel(reason)})`:""}`,
                          job_id: jobId }),
                      });
                      const body = await res.json().catch(()=>({}));
                      if (!res.ok || body?.error) throw new Error(body?.error || `HTTP ${res.status}`);
                      creditApplied = true;
                      await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${jobId}`,{method:"PATCH",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({credited:true})});
                      showToast(body.idempotent
                        ? "A credit already existed for this visit — no new credit posted."
                        : `$${perVisit.toFixed(2)} credit applied to Stripe balance — will deduct from next invoice`);
                      onRefreshInvoices&&onRefreshInvoices();
                    } catch (e) {
                      showToast(`Credit failed — not applied: ${e.message||e}`,"error");
                    }
                  }
                }
                // 3. History reflects what ACTUALLY happened.
                const historyDesc = isCreditMode
                  ? `Credit issued on completed visit ${schedDate} (${reason})${credit&&!creditApplied?" — CREDIT FAILED":""}`
                  : `Visit on ${schedDate} marked as skipped${creditApplied?" (credit applied)":credit?" — CREDIT FAILED":""}`;
                await fetch(`${SUPABASE_URL}/rest/v1/client_history`,{method:"POST",headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},body:JSON.stringify({client_id:client.id,event_type:isCreditMode?"credit_issued":"skipped_visit",description:historyDesc,new_value:isCreditMode ? reason : skipReasonLabel(reason)+(skipNote?` — ${skipNote}`:"")})});
                const all = await fetch(`${SUPABASE_URL}/rest/v1/jobs?client_id=eq.${client.id}&order=scheduled_date.desc&limit=200`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json());
                if (Array.isArray(all)) { const mine=all.filter(r=>r.client_id===client.id); const byDate={}; mine.forEach(r=>{byDate[r.scheduled_date+(r.service_type||"cleanup")]=r;}); setVisitRecords(byDate); setVisits(mine); }
                setSkipModal(null);
              }}
            />
          )}

          {/* Job Detail Modal — opened by clicking any visit row.
              Uses the same modal as the Schedule + Technician Routes
              tabs so the operator sees one consistent view of a job
              regardless of where they clicked from. */}
          {jobDetailId && (() => {
            const job = visits.find(v => v.id === jobDetailId);
            if (!job) { setJobDetailId(null); return null; }
            return (
              <JobDetailModal
                job={job}
                clients={[client]}
                technicians={technicians}
                pricing={pricing}
                onClose={()=>setJobDetailId(null)}
                onUpdated={async()=>{
                  // Refetch visits so any status/note/tech changes
                  // reflect immediately in the table.
                  const statusFilter = "status=in.(completed,skipped,missed)";
                  const all = await fetch(
                    `${SUPABASE_URL}/rest/v1/jobs?client_id=eq.${client.id}&${statusFilter}&order=scheduled_date.desc&limit=200`,
                    { headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`} }
                  ).then(r=>r.json());
                  if (Array.isArray(all)) {
                    const byDate = {};
                    all.forEach(r=>{byDate[r.scheduled_date+(r.service_type||"cleanup")]=r;});
                    setVisitRecords(byDate);
                    setVisits(all);
                  }
                  // also refresh the header rollup so the day-header duration + proof
                  // photo in the Visits list reflect the modal's writes immediately.
                  const hs = await fetch(
                    `${SUPABASE_URL}/rest/v1/job_headers?client_id=eq.${client.id}&select=scheduled_date,photo_url,duration_mins&order=scheduled_date.desc&limit=200`,
                    { headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`} }
                  ).then(r=>r.json()).catch(()=>null);
                  if (Array.isArray(hs)) {
                    const m={}, d={};
                    hs.forEach(h=>{ if (h.photo_url && !(h.scheduled_date in m)) m[h.scheduled_date]=h.photo_url; if (h.duration_mins!=null && !(h.scheduled_date in d)) d[h.scheduled_date]=h.duration_mins; });
                    setHeaderPhotos(m); setHeaderDur(d);
                  }
                }}
                showToast={showToast}
              />
            );
          })()}
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {tab==="history" && (
        <div className="card">
          <div className="card-title">Client History</div>
          {loadingHist&&<div style={{padding:20,textAlign:"center",color:"#8993a4",fontSize:13}}>Loading history...</div>}
          {!loadingHist&&history.length===0&&<div className="empty"><p>No history recorded yet</p></div>}
          {!loadingHist&&history.map((h,i)=>(
            <div key={h.id} style={{display:"flex",gap:12,padding:"10px 0",borderBottom:i<history.length-1?"1px solid #f4f5f7":"none"}}>
              <div style={{width:32,height:32,borderRadius:"50%",background:"#f4f5f7",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>
                {EVENT_ICONS[h.event_type]||"📋"}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:500,color:"#253858",wordBreak:"break-word"}}>{h.description}</div>
                {h.old_value&&h.new_value&&(
                  <div style={{fontSize:11,color:"#8993a4",marginTop:2,wordBreak:"break-word"}}>
                    <span style={{textDecoration:"line-through"}}>{h.old_value}</span>
                    {" → "}
                    <span style={{color:NAVY,fontWeight:600}}>{h.new_value}</span>
                  </div>
                )}
                <div style={{fontSize:11,color:"#b3bac5",marginTop:2}}>
                  {new Date(h.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"2-digit",minute:"2-digit"})}
                  {/* Phones: the event-type label rides under the timestamp instead of a
                      nowrap right column that squeezes the description to ~60px. */}
                  {isMobile && <span style={{textTransform:"capitalize",fontWeight:500}}> · {(h.event_type||"").replace(/_/g," ")}</span>}
                </div>
              </div>
              {!isMobile && (
                <div style={{fontSize:11,color:"#b3bac5",whiteSpace:"nowrap",textTransform:"capitalize",fontWeight:500}}>
                  {(h.event_type||"").replace(/_/g," ")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </div>

      {/* ── PAYMENT LINK MODAL ── */}
      {sendPaymentLink && (
        <PaymentLinkModal
          client={client}
          invoices={invoices}
          onClose={()=>setSendPaymentLink(false)}
          onSent={()=>{ setSendPaymentLink(false); showToast("Payment link sent to "+client.email); }}
          showToast={showToast}
          orgName={orgName}
        />
      )}

      {/* ── PAUSE REASON MODAL ── */}
      {showPauseReason&&(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowPauseReason(false)}>
          <div className="modal" style={{maxWidth:400}}>
            <div className="modal-header">
              <h3>⏸ Pause Service</h3>
              <button className="close-btn" onClick={()=>setShowPauseReason(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Reason for pausing</label>
                <select value={pauseReason} onChange={e=>setPauseReason(e.target.value)}>
                  <option value="">— Select reason —</option>
                  <option value="vacation">Vacation / Travel</option>
                  <option value="financial">Financial reasons</option>
                  <option value="moving">Moving soon</option>
                  <option value="no_dogs">No longer has dogs</option>
                  <option value="seasonal">Seasonal pause</option>
                  <option value="dissatisfied">Dissatisfied with service</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setShowPauseReason(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={()=>{ onStatusChange(client.id,"pause",pauseReason); setShowPauseReason(false); }}>Confirm Pause</button>
            </div>
          </div>
        </div>
      )}

      {/* ── CHARGE MODAL ── */}
      {showCharge && (
        <ChargeClientModal
          client={client}
          invoices={invoices}
          onClose={()=>setShowCharge(false)}
          onSuccess={(inv)=>{
            setShowCharge(false);
            showToast(`Charged $${Number(inv.amount).toFixed(2)} successfully!`);
            // Refresh history
            fetch(`${SUPABASE_URL}/rest/v1/client_history?client_id=eq.${client.id}&order=created_at.desc&limit=100`,{headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}}).then(r=>r.json()).then(data=>{if(Array.isArray(data))setHistory(data);});
            // Refresh invoices so total spend updates
            onRefreshInvoices && onRefreshInvoices();
            setTab("billing");
          }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DEODORIZER CARD — state-aware lifecycle UI
// ═══════════════════════════════════════════════════════════════════
// Renders one of three states based on client.deo_status:
//   - null or "cancelled" → "+ Add Deodorizer" with frequency picker
//   - "active"            → freq + price display + Pause / Cancel / Change Freq
//   - "paused"            → paused banner + Resume / Cancel buttons
// All actions go through onDeoAction(action, payload) which posts to
// update-subscription and refreshes the client row when the API returns.
function DeodorizerCard({ client, pricing, onDeoAction }) {
  const [addingFreq,     setAddingFreq]     = useState(false);
  const [pickedFreq,     setPickedFreq]     = useState("biweekly");
  const [changingFreq,   setChangingFreq]   = useState(false);
  const [busy,           setBusy]           = useState(false);

  const status      = client.deo_status; // 'active' | 'paused' | 'cancelled' | null
  const freq        = client.deo_frequency;
  const cleanupFreq = client.frequency || "weekly";
  const freqOptions = DEO_FREQ_OPTIONS[cleanupFreq] || ["biweekly", "monthly"];
  const freqLabel   = (f) => f === "weekly" ? "Weekly" : f === "biweekly" ? "Bi-Weekly" : "Monthly";

  const perVisitRate = freq ? (pricing?.deoPerVisit?.[freq] ?? DEO_PER_VISIT[freq]) : null;
  const monthlyRate  = freq ? getDeoMonthly(freq, pricing) : null;

  async function run(action, payload) {
    setBusy(true);
    await onDeoAction(action, { client_id: client.id, ...payload });
    setBusy(false);
    setAddingFreq(false);
    setChangingFreq(false);
  }

  // ── STATE 1: No deo sub yet (or previously cancelled) ─────────
  if (!status || status === "cancelled") {
    return (
      <div className="card">
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div className="card-title" style={{marginBottom:0}}>💨 Deodorizer</div>
          {status === "cancelled" && <span className="badge badge-gray">Cancelled</span>}
        </div>
        {!addingFreq ? (
          <div>
            <div style={{fontSize:13,color:"#42526e",marginBottom:12}}>
              No deodorizer subscription. Adding one creates a separate Stripe subscription, billed on the same date as cleanup.
            </div>
            <button className="btn btn-gold" onClick={()=>setAddingFreq(true)} disabled={busy}>+ Add Deodorizer</button>
          </div>
        ) : (
          <div>
            <div className="form-group" style={{marginBottom:12}}>
              <label>Deodorizer Frequency</label>
              <select value={pickedFreq} onChange={e=>setPickedFreq(e.target.value)}>
                {freqOptions.map(f => <option key={f} value={f}>{freqLabel(f)}</option>)}
              </select>
              <div style={{fontSize:11,color:"#8993a4",marginTop:5}}>
                ${(pricing?.deoPerVisit?.[pickedFreq] ?? DEO_PER_VISIT[pickedFreq])}/visit
                · ${getDeoMonthly(pickedFreq, pricing).toFixed(2)}/month
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn btn-gold" disabled={busy} onClick={()=>run("add_deo", { deo_frequency: pickedFreq })}>
                {busy ? "Adding..." : "Confirm — Add Deodorizer"}
              </button>
              <button className="btn btn-ghost" disabled={busy} onClick={()=>setAddingFreq(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── STATE 2: Active ───────────────────────────────────────────
  if (status === "active") {
    return (
      <div className="card">
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div className="card-title" style={{marginBottom:0}}>💨 Deodorizer</div>
          <span className="badge badge-green">Active</span>
        </div>
        <div style={{display:"flex",gap:10,padding:"8px 0",borderBottom:"1px solid #f4f5f7"}}>
          <span style={{fontSize:12,color:"#8993a4",width:110,flexShrink:0}}>Frequency</span>
          <span style={{fontSize:13,fontWeight:600,color:"#253858"}}>{freqLabel(freq)}</span>
        </div>
        <div style={{display:"flex",gap:10,padding:"8px 0",borderBottom:"1px solid #f4f5f7"}}>
          <span style={{fontSize:12,color:"#8993a4",width:110,flexShrink:0}}>Rate</span>
          <span style={{fontSize:13,fontWeight:600,color:"#253858"}}>${perVisitRate}/visit · ${monthlyRate.toFixed(2)}/month</span>
        </div>
        {changingFreq ? (
          <div style={{marginTop:14,padding:"12px 14px",background:"#f4f5f7",borderRadius:8}}>
            <label style={{fontSize:12,fontWeight:500,color:"#42526e",display:"block",marginBottom:5}}>Change to</label>
            <select value={pickedFreq} onChange={e=>setPickedFreq(e.target.value)}>
              {freqOptions.map(f => <option key={f} value={f}>{freqLabel(f)}</option>)}
            </select>
            <div style={{fontSize:11,color:"#8993a4",marginTop:5,marginBottom:10}}>
              New rate: ${(pricing?.deoPerVisit?.[pickedFreq] ?? DEO_PER_VISIT[pickedFreq])}/visit
              · ${getDeoMonthly(pickedFreq, pricing).toFixed(2)}/month
            </div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn btn-gold" disabled={busy || pickedFreq===freq} onClick={()=>run("change_deo_frequency", { new_deo_frequency: pickedFreq })}>
                {busy ? "Saving..." : "Confirm Change"}
              </button>
              <button className="btn btn-ghost" disabled={busy} onClick={()=>setChangingFreq(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{display:"flex",gap:8,marginTop:14,flexWrap:"wrap"}}>
            <button className="btn btn-ghost" disabled={busy} onClick={()=>{ setPickedFreq(freq||"biweekly"); setChangingFreq(true); }}>
              Change Frequency
            </button>
            <button className="btn btn-ghost" disabled={busy} onClick={()=>{
              if (window.confirm("Pause the deodorizer subscription? Cleanup will continue. You can resume anytime.")) {
                run("pause_deo", {});
              }
            }}>
              ⏸ Pause
            </button>
            <button className="btn btn-danger" disabled={busy} onClick={()=>{
              if (window.confirm("Cancel the deodorizer subscription? Cleanup will continue. This cannot be undone (you'd need to add a new deodorizer subscription).")) {
                run("cancel_deo", {});
              }
            }}>
              Cancel Deodorizer
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── STATE 3: Paused ───────────────────────────────────────────
  if (status === "paused") {
    return (
      <div className="card">
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div className="card-title" style={{marginBottom:0}}>💨 Deodorizer</div>
          <span className="badge badge-gold">Paused</span>
        </div>
        <div style={{background:"#fffae6",border:"1px solid #fde047",borderRadius:6,padding:"10px 12px",marginBottom:14,fontSize:13,color:"#974f0c"}}>
          ⏸ Deodorizer billing is paused. Cleanup continues normally. No deodorizer visits will be generated until you resume.
        </div>
        <div style={{display:"flex",gap:10,padding:"8px 0",borderBottom:"1px solid #f4f5f7"}}>
          <span style={{fontSize:12,color:"#8993a4",width:110,flexShrink:0}}>Frequency</span>
          <span style={{fontSize:13,fontWeight:600,color:"#253858"}}>{freqLabel(freq)} (when resumed)</span>
        </div>
        <div style={{display:"flex",gap:8,marginTop:14}}>
          <button className="btn btn-gold" disabled={busy} onClick={()=>run("resume_deo", {})}>
            ▶ Resume
          </button>
          <button className="btn btn-danger" disabled={busy} onClick={()=>{
            if (window.confirm("Cancel the paused deodorizer subscription entirely? Cleanup will continue.")) {
              run("cancel_deo", {});
            }
          }}>
            Cancel Deodorizer
          </button>
        </div>
      </div>
    );
  }

  // Fallback for unexpected statuses (e.g. 'incomplete', 'past_due')
  return (
    <div className="card">
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
        <div className="card-title" style={{marginBottom:0}}>💨 Deodorizer</div>
        <span className="badge badge-gray" style={{textTransform:"capitalize"}}>{status}</span>
      </div>
      <div style={{fontSize:13,color:"#42526e"}}>
        Deodorizer subscription is in an unusual state. Check Stripe directly or contact support.
      </div>
    </div>
  );
}
